/* eslint-disable no-nested-ternary */
import React from 'react';

import type { FormField } from '../types/pdf.types';

interface DropdownFieldOverlayProps {
  field: FormField;
  value: string | string[];
  onChange: (value: string | string[]) => void;
  scale: number;
  isZooming?: boolean;
}

const DropdownFieldOverlay: React.FC<DropdownFieldOverlayProps> = ({
  field,
  value,
  onChange,
  scale,
  isZooming = false,
}) => {
  if (field.hidden) return null;

  const [x, y, width, height] = field.rect;
  const scaledX = x * scale;
  const scaledY = y * scale;
  const scaledWidth = width * scale;
  const scaledHeight = height * scale;

  const isReadOnly = field.readOnly === true;
  const isRequired = field.required === true;
  const isNoExport = field.noExport === true;
  const isCombo = field.isCombo !== undefined ? field.isCombo : true;
  const isMultiSelect = field.multiSelect === true;
  const isEditable = field.editable === true;

  const fontSizePt = (field.fontSize || 12) * 0.5;

  const overlayBorder = isRequired
    ? '1px solid #faad14'
    : isNoExport
    ? '1px dashed #b37feb'
    : '1px solid #d9d9d9';

  const overlayStyle = {
    left: `${scaledX}px`,
    top: `${scaledY}px`,
    width: `${scaledWidth}px`,
    height: `${scaledHeight}px`,
    padding: '2px 0px',
    fontSize: `${fontSizePt * scale}pt`,
    border: overlayBorder,
    backgroundColor: isReadOnly
      ? 'rgba(240, 240, 240, 0.9)'
      : 'rgba(255, 255, 255, 0.9)',
    opacity: isZooming ? 0 : isReadOnly ? 0.6 : 1,
    pointerEvents: isZooming ? 'none' : 'auto',
    cursor: isReadOnly ? 'not-allowed' : isEditable ? 'text' : 'pointer',
  };

  const options = field.options || ['Select an option'];
  const listId = `${field.id}-datalist`;

  const titleParts = [field.name];
  if (isRequired) titleParts.push('Required');
  if (isNoExport) titleParts.push('No export');
  if (isReadOnly) titleParts.push('Read-only');
  const title = titleParts.join(' • ');

  if (isCombo && isEditable) {
    return (
      <>
        <input
          value={Array.isArray(value) ? value.join(', ') : value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="pdf-field-overlay pdf-field-input"
          style={overlayStyle}
          title={title}
          list={listId}
          disabled={isReadOnly}
          readOnly={isReadOnly}
          required={isRequired}
          aria-required={isRequired}
          spellCheck={field.doNotSpellCheck ? false : undefined}
        />
        <datalist id={listId}>
          {options.map((opt, idx) => (
            <option key={idx} value={opt} />
          ))}
        </datalist>
      </>
    );
  }

  const normalizedMultiValue = isMultiSelect
    ? Array.isArray(value)
      ? value
      : typeof value === 'string' && value !== ''
      ? [value]
      : []
    : [];

  return (
    <select
      value={
        isMultiSelect ? normalizedMultiValue : Array.isArray(value) ? '' : value
      }
      onChange={(e) => {
        if (isMultiSelect) {
          const selected = Array.from(e.currentTarget.selectedOptions).map(
            (opt) => opt.value,
          );
          onChange(selected);
        } else {
          onChange(e.target.value);
        }
      }}
      className="pdf-field-overlay pdf-field-select"
      style={overlayStyle}
      title={title}
      disabled={isReadOnly}
      required={isRequired}
      aria-required={isRequired}
      multiple={isMultiSelect}
      size={!isCombo ? Math.min(Math.max(options.length, 2), 8) : undefined}
    >
      {isCombo && !isMultiSelect && <option value="">-- Select --</option>}
      {options.map((opt, idx) => (
        <option key={idx} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  );
};

const areEqual = (
  prevProps: DropdownFieldOverlayProps,
  nextProps: DropdownFieldOverlayProps,
) => {
  return (
    prevProps.field.id === nextProps.field.id &&
    prevProps.value === nextProps.value &&
    prevProps.scale === nextProps.scale &&
    prevProps.isZooming === nextProps.isZooming
  );
};

export default React.memo(DropdownFieldOverlay, areEqual);
